import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

/**
 * List of `REMOTE_ENVIRONMENT_NAME` that have some features like Apify runner & project sync enabled by default
 * Other environments like PR or local will have to be explicitly enabled by setting env var
 * */
export const envWhitelist = ['production', 'staging', 'sandbox', 'develop'];

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    DATABASE_URL: z
      .string()
      .url()
      .refine(
        (str) => !str.includes('YOUR_POSTGRES_URL_HERE'),
        'You forgot to change the default URL',
      ),
    MASTRA_DATABASE_URL: z.string().url().optional(),
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
    APIFY_API_TOKEN: z.string().min(1),
    FIRECRAWL_API_KEY: z.string().min(1),
    FIRECRAWL_WEBHOOK_SECRET: z.string().optional(),
    FIREWORKS_API_KEY: z.string().min(1).optional(),
    FIREWORKS_ACCOUNT_ID: z.string().min(1).optional(),
    TOGETHER_API_KEY: z.string().min(1).optional(),
    WANDB_API_KEY: z.string().min(1).optional(),
    MODAL_TOKEN_SECRET: z.string().min(1).optional(),
    MODAL_FINETUNE_ENDPOINT: z.string().url().optional(),
    AZURE_VISION_KEY: z.string().min(1),
    AZURE_VISION_URL: z.string().min(1),
    GCP_DOCUMENT_AI_URI: z.string().min(1),
    GCP_ARCHIVE_BUCKET_NAME: z.string().min(1),
    GOOGLE_SERVICE_KEY: z.string().min(1),
    RAPID_API_KEY: z.string().min(1),
    AWS_BUCKET_NAME: z.string().min(1),
    // AWS_EMAIL_ARCHIVE_BUCKET_NAME: z.string().min(1),
    AWS_AGENT_BUCKET_NAME: z.string().min(1).optional(),
    AWS_SECRET_ACCESS_KEY: z.string().min(1),
    AWS_ACCESS_KEY_ID: z.string().min(1),
    EMAIL_RECIPIENT: z.string().min(1).default('alex@tryprotege.com'),
    QDRANT_URL: z.string().min(1),
    QDRANT_PORT: z.string().optional(),
    QDRANT_SIMILAR_USAGE_COLLECTION_NAME: z.string().min(1),
    QDRANT_API_KEY: z.string().optional(),
    CORS_ALLOWED_URLS: z.string().optional(),
    REDIS_URL: z.string().min(1),
    LOGTAIL_SOURCE_TOKEN: z.string().optional(),
    LOGTAIL_ENDPOINT: z.string().optional(),
    AUTH0_SECRET: z.string().min(1),
    AUTH0_BASE_URL: z.string().min(1),
    AUTH0_ISSUER_BASE_URL: z.string().min(1),
    AUTH0_CLIENT_ID: z.string().min(1),
    AUTH0_CLIENT_SECRET: z.string().min(1),
    AUTH0_MANAGEMENT_DOMAIN: z.string().min(1),
    AUTH0_MANAGEMENT_CLIENT_ID: z.string().min(1),
    AUTH0_MANAGEMENT_CLIENT_SECRET: z.string().min(1),
    // Hookshot user sync (companion app sharing this Auth0 tenant). Optional: sync is
    // skipped when unset — see src/server/utils/hookshotSync.ts.
    HOOKSHOT_CORE_API_URL: z.string().url().optional(),
    HOOKSHOT_USER_SYNC_TOKEN: z.string().min(1).optional(),
    JIRA_CLIENT_ID: z.string().min(1),
    JIRA_CLIENT_SECRET: z.string().min(1),
    ASANA_CLIENT_ID: z.string().min(1),
    ASANA_CLIENT_SECRET: z.string().min(1),
    SMARTSHEET_CLIENT_ID: z.string().min(1),
    SMARTSHEET_CLIENT_SECRET: z.string().min(1),
    FRAMEIO_CLIENT_ID: z.string().min(1),
    FRAMEIO_CLIENT_SECRET: z.string().min(1),
    DOPPLER_SERVICE_TOKEN: z.string().min(1),
    DOPPLER_PROJECT_NAME: z.string().min(1),
    DOPPLER_CONFIG_NAME: z.string().min(1),
    DOPPLER_ECOMMS_SERVICE_TOKEN: z.string().min(1),
    DOPPLER_ECOMMS_PROJECT_NAME: z.string().min(1),
    DOPPLER_ECOMMS_CONFIG_NAME: z.string().min(1),
    DOPPLER_SHAREABLE_LINKS_SERVICE_TOKEN: z.string().min(1),
    DOPPLER_SHAREABLE_LINKS_PROJECT_NAME: z.string().min(1),
    DOPPLER_SHAREABLE_LINKS_CONFIG_NAME: z.string().min(1),
    GOOGLE_APIS_CLIENT_ID: z.string().min(1),
    GOOGLE_APIS_CLIENT_SECRET: z.string().min(1),
    WRIKE_CLIENT_ID: z.string().min(1),
    WRIKE_CLIENT_SECRET: z.string().min(1),
    API_PUBLIC_KEY: z.string().min(1),
    API_PRIVATE_KEY: z.string().optional(),
    SHAREPOINT_CLIENT_ID: z.string().min(1),
    SHAREPOINT_CLIENT_SECRET: z.string().min(1),
    POSTHOG_API_KEY: z.string().optional(),
    GOOGLE_API_INT_TOKEN: z.string().optional(),
    FRAMEIO_INT_TOKEN: z.string().optional(),
    PUSHER_APP_ID: z.string().min(1),
    PUSHER_APP_KEY: z.string().min(1),
    PUSHER_APP_SECRET: z.string().optional(),
    PUSHER_APP_CLUSTER: z.string().min(1),
    DEEPL_API_KEY: z.string().optional(),
    PROXY_URL: z.string().optional(),
    REMOTE_SERVICE_NAME: z.string().optional(),
    REMOTE_ENVIRONMENT_NAME: z.string().optional(),
    LITELLM_BASE_URL: z.string(),
    LITELLM_API_KEY: z.string(),
    AIRTABLE_CLIENT_ID: z.string().min(1),
    AIRTABLE_CLIENT_SECRET: z.string().min(1),
    MIRROR_ENVIRONMENT_URL: z.string().optional(),
    MIRROR_ENVIRONMENT_API_PRIVATE_KEY: z.string().optional(),
    FIGMA_CLIENT_ID: z.string().min(1),
    FIGMA_CLIENT_SECRET: z.string().min(1),
    SLACK_WEBHOOK_URL_PORTAL_UPDATES: z.string().optional(),
    TEMPORAL_URL: z.string().optional(),
    SYNC_PROJECTS_CRON: z.string().optional(),
    DISABLE_APIFY_WORKERS: z.boolean().optional().default(false),
    HEARTBEAT_URL: z.string().optional(),
    SCANNER_JOB_HEARTBEAT_CONFIG: z.preprocess((val) => {
      if (!val || typeof val !== 'string') {
        return {};
      }
      try {
        return JSON.parse(val);
      } catch (e) {
        console.error('Invalid JSON string for SCANNER_JOB_HEARTBEAT_CONFIG', val);
        return {};
      }
    }, z.record(z.string().url())),
    TEMPORAL_API_KEY: z.string().optional(),
    TEMPORAL_NAMESPACE: z.string().optional(),
    TEMPORAL_WORKER_URL: z.string().optional(),
    WEBHOOK_SECRET_ENCRYPTION_KEY: z.string().min(1),

    // Stripe integration (optional - only required in environments with Stripe enabled)
    STRIPE_SECRET_KEY: z.string().min(1).optional(),
    STRIPE_PUBLISHABLE_KEY: z.string().min(1).optional(),
    STRIPE_WEBHOOK_SECRET: z.string().min(1).optional(),
    STRIPE_PRICE_ID_TIERED: z.string().min(1).optional(), // Single tiered price for all seat quantities

    // Plaid integration
    PLAID_CLIENT_ID: z.string().optional(),
    PLAID_SECRET: z.string().optional(),
    PLAID_ENV: z.enum(['sandbox', 'production']).optional(),

    // Crypto exchange APIs
    COINBASE_CLIENT_ID: z.string().optional(),
    COINBASE_CLIENT_SECRET: z.string().optional(),

    // Blockchain data providers
    MORALIS_API_KEY: z.string().optional(),

    // GitBook API
    GITBOOK_API_TOKEN: z.string().optional(),
    GITBOOK_SPACE_ID: z.string().optional(),

    // For testing Mastra agent and MCP
    OVERRIDE_AGENT_TENANT_ID: z.string().optional(),
    OVERRIDE_AGENT_USER_ID: z.string().optional(),
    OVERRIDE_AGENT_WORKSPACE_ID: z.string().optional(),

    // Dev auth bypass — allows accessing the full dashboard without Auth0 in local development.
    // ALL four conditions must be true for the bypass to activate (see src/server/devAuth.ts):
    //   1. DEV_AUTH_BYPASS=true
    //   2. NODE_ENV=development
    //   3. REMOTE_ENVIRONMENT_NAME is absent or "local"
    //   4. DEV_TENANT_ID + DEV_USER_ID + DEV_WORKSPACE_ID are all set
    DEV_AUTH_BYPASS: z.boolean().optional().default(false),
    DEV_TENANT_ID: z.string().uuid().optional(),
    DEV_USER_ID: z.string().uuid().optional(),
    DEV_WORKSPACE_ID: z.string().uuid().optional(),

    // Agent model metrics API (for Modal to report training results)
    AGENT_MODEL_METRICS_API_KEY: z.string().optional(),

    // Modal orchestrator service URL (for managing LoRA deployments)
    MODAL_ORCHESTRATOR_URL: z.string().url().optional(),

    // Workflow concurrency settings
    MASTRA_WORKFLOW_CONCURRENCY: z.number().min(1).max(128).optional().default(16),

    // Pagination settings
    DEFAULT_PAGE_SIZE: z.number().min(1).max(1000).optional().default(100),
    MAX_PAGE_SIZE: z.number().min(1).max(1000).optional().default(500),

    // Feature flags
    DISABLE_SIMILAR_FEEDBACK: z.boolean().optional().default(false),
    DISABLE_QDRANT_DOCUMENT_PROCESSING: z.boolean().optional().default(false),

    /** Enable apify runner. Set this to `true` to enable for PRs and local */
    ENABLE_APIFY_RUNNER: z
      .boolean()
      .optional()
      .default(envWhitelist.includes(process.env.REMOTE_ENVIRONMENT_NAME ?? '')),
    /** Enable project sync. Set this to `true` to enable for PRs and local */
    ENABLE_SYNC_PROJECTS: z
      .boolean()
      .optional()
      .default(envWhitelist.includes(process.env.REMOTE_ENVIRONMENT_NAME ?? '')),
    /** Enable agent model fine-tuning. Set this to `true` to enable for PRs and local */
    ENABLE_AGENT_FINETUNING_SCHEDULE: z
      .boolean()
      .optional()
      .default(
        ['develop', 'staging', 'production'].includes(process.env.REMOTE_ENVIRONMENT_NAME ?? ''),
      ),
    ENABLE_EMAIL_DIGEST_SCHEDULE: z
      .boolean()
      .optional()
      .default(envWhitelist.includes(process.env.REMOTE_ENVIRONMENT_NAME ?? '')),
    ENABLE_ECOMMS_INGEST_SCHEDULE: z
      .boolean()
      .optional()
      .default(process.env.REMOTE_ENVIRONMENT_NAME === 'develop'),
    PORT: z.number(),
    MASTRA_PLAYGROUND_URL: z.string().optional(),
    PROTEGE_MCP_SERVER_URL: z.string().optional(),
    NEXUS_SERVER_URL: z.string().optional(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_REMOTE_ENVIRONMENT_NAME: z.string().optional(),
    NEXT_PUBLIC_MIRROR_ENVIRONMENT_NAME: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_API_KEY: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_API_HOST: z.string().optional(),
    // Mirrors server-side DEV_AUTH_BYPASS so client components can detect bypass mode
    NEXT_PUBLIC_DEV_AUTH_BYPASS: z.boolean().optional().default(false),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    DATABASE_URL: process.env.DATABASE_URL,
    MASTRA_DATABASE_URL: process.env.MASTRA_DATABASE_URL,
    NODE_ENV: process.env.NODE_ENV,
    APIFY_API_TOKEN: process.env.APIFY_API_TOKEN,
    FIRECRAWL_API_KEY: process.env.FIRECRAWL_API_KEY,
    FIRECRAWL_WEBHOOK_SECRET: process.env.FIRECRAWL_WEBHOOK_SECRET,
    QDRANT_API_KEY: process.env.QDRANT_API_KEY,
    FIREWORKS_API_KEY: process.env.FIREWORKS_API_KEY,
    FIREWORKS_ACCOUNT_ID: process.env.FIREWORKS_ACCOUNT_ID,
    TOGETHER_API_KEY: process.env.TOGETHER_API_KEY,
    WANDB_API_KEY: process.env.WANDB_API_KEY,
    MODAL_TOKEN_SECRET: process.env.MODAL_TOKEN_SECRET,
    MODAL_FINETUNE_ENDPOINT: process.env.MODAL_FINETUNE_ENDPOINT,
    AZURE_VISION_KEY: process.env.AZURE_VISION_KEY,
    AZURE_VISION_URL: process.env.AZURE_VISION_URL,
    GCP_ARCHIVE_BUCKET_NAME: process.env.GCP_ARCHIVE_BUCKET_NAME,
    GCP_DOCUMENT_AI_URI: process.env.GCP_DOCUMENT_AI_URI,
    GOOGLE_SERVICE_KEY: process.env.GOOGLE_SERVICE_KEY,
    RAPID_API_KEY: process.env.RAPID_API_KEY,
    AWS_BUCKET_NAME: process.env.AWS_BUCKET_NAME,
    // AWS_EMAIL_ARCHIVE_BUCKET_NAME: process.env.AWS_EMAIL_ARCHIVE_BUCKET_NAME,
    AWS_AGENT_BUCKET_NAME: process.env.AWS_AGENT_BUCKET_NAME,
    AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
    AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
    EMAIL_RECIPIENT: process.env.EMAIL_RECIPIENT,
    QDRANT_URL: process.env.QDRANT_URL,
    QDRANT_PORT: process.env.QDRANT_PORT,
    QDRANT_SIMILAR_USAGE_COLLECTION_NAME: process.env.QDRANT_SIMILAR_USAGE_COLLECTION_NAME,
    REDIS_URL: process.env.REDIS_URL,
    LOGTAIL_SOURCE_TOKEN: process.env.LOGTAIL_SOURCE_TOKEN,
    LOGTAIL_ENDPOINT: process.env.LOGTAIL_ENDPOINT,
    AUTH0_SECRET: process.env.AUTH0_SECRET,
    AUTH0_BASE_URL: process.env.AUTH0_BASE_URL,
    AUTH0_ISSUER_BASE_URL: process.env.AUTH0_ISSUER_BASE_URL,
    AUTH0_CLIENT_ID: process.env.AUTH0_CLIENT_ID,
    AUTH0_CLIENT_SECRET: process.env.AUTH0_CLIENT_SECRET,
    AUTH0_MANAGEMENT_DOMAIN: process.env.AUTH0_MANAGEMENT_DOMAIN,
    AUTH0_MANAGEMENT_CLIENT_ID: process.env.AUTH0_MANAGEMENT_CLIENT_ID,
    AUTH0_MANAGEMENT_CLIENT_SECRET: process.env.AUTH0_MANAGEMENT_CLIENT_SECRET,
    HOOKSHOT_CORE_API_URL: process.env.HOOKSHOT_CORE_API_URL,
    HOOKSHOT_USER_SYNC_TOKEN: process.env.HOOKSHOT_USER_SYNC_TOKEN,
    JIRA_CLIENT_ID: process.env.JIRA_CLIENT_ID,
    JIRA_CLIENT_SECRET: process.env.JIRA_CLIENT_SECRET,
    DOPPLER_SERVICE_TOKEN: process.env.DOPPLER_SERVICE_TOKEN,
    DOPPLER_PROJECT_NAME: process.env.DOPPLER_PROJECT_NAME,
    DOPPLER_CONFIG_NAME: process.env.DOPPLER_CONFIG_NAME,
    DOPPLER_ECOMMS_SERVICE_TOKEN: process.env.DOPPLER_ECOMMS_SERVICE_TOKEN,
    DOPPLER_ECOMMS_PROJECT_NAME: process.env.DOPPLER_ECOMMS_PROJECT_NAME,
    DOPPLER_ECOMMS_CONFIG_NAME: process.env.DOPPLER_ECOMMS_CONFIG_NAME,
    DOPPLER_SHAREABLE_LINKS_SERVICE_TOKEN: process.env.DOPPLER_SHAREABLE_LINKS_SERVICE_TOKEN,
    DOPPLER_SHAREABLE_LINKS_PROJECT_NAME: process.env.DOPPLER_SHAREABLE_LINKS_PROJECT_NAME,
    DOPPLER_SHAREABLE_LINKS_CONFIG_NAME: process.env.DOPPLER_SHAREABLE_LINKS_CONFIG_NAME,
    ASANA_CLIENT_ID: process.env.ASANA_CLIENT_ID,
    ASANA_CLIENT_SECRET: process.env.ASANA_CLIENT_SECRET,
    GOOGLE_APIS_CLIENT_ID: process.env.GOOGLE_APIS_CLIENT_ID,
    GOOGLE_APIS_CLIENT_SECRET: process.env.GOOGLE_APIS_CLIENT_SECRET,
    SMARTSHEET_CLIENT_ID: process.env.SMARTSHEET_CLIENT_ID,
    SMARTSHEET_CLIENT_SECRET: process.env.SMARTSHEET_CLIENT_SECRET,
    FRAMEIO_CLIENT_ID: process.env.FRAMEIO_CLIENT_ID,
    FRAMEIO_CLIENT_SECRET: process.env.FRAMEIO_CLIENT_SECRET,
    WRIKE_CLIENT_ID: process.env.WRIKE_CLIENT_ID,
    WRIKE_CLIENT_SECRET: process.env.WRIKE_CLIENT_SECRET,
    API_PUBLIC_KEY: process.env.API_PUBLIC_KEY,
    API_PRIVATE_KEY: process.env.API_PRIVATE_KEY,
    SHAREPOINT_CLIENT_ID: process.env.SHAREPOINT_CLIENT_ID,
    SHAREPOINT_CLIENT_SECRET: process.env.SHAREPOINT_CLIENT_SECRET,
    POSTHOG_API_KEY: process.env.POSTHOG_API_KEY,
    CORS_ALLOWED_URLS: process.env.CORS_ALLOWED_URLS,
    GOOGLE_API_INT_TOKEN: process.env.GOOGLE_API_INT_TOKEN,
    FRAMEIO_INT_TOKEN: process.env.FRAMEIO_INT_TOKEN,
    PUSHER_APP_ID: process.env.PUSHER_APP_ID,
    PUSHER_APP_KEY: process.env.PUSHER_APP_KEY,
    PUSHER_APP_SECRET: process.env.PUSHER_APP_SECRET,
    PUSHER_APP_CLUSTER: process.env.PUSHER_APP_CLUSTER,
    DEEPL_API_KEY: process.env.DEEPL_API_KEY,
    PROXY_URL: process.env.PROXY_URL,
    REMOTE_SERVICE_NAME: process.env.REMOTE_SERVICE_NAME,
    REMOTE_ENVIRONMENT_NAME: process.env.REMOTE_ENVIRONMENT_NAME,
    LITELLM_BASE_URL: process.env.LITELLM_BASE_URL,
    LITELLM_API_KEY: process.env.LITELLM_API_KEY,
    AIRTABLE_CLIENT_ID: process.env.AIRTABLE_CLIENT_ID,
    AIRTABLE_CLIENT_SECRET: process.env.AIRTABLE_CLIENT_SECRET,
    MIRROR_ENVIRONMENT_URL: process.env.MIRROR_ENVIRONMENT_URL,
    MIRROR_ENVIRONMENT_API_PRIVATE_KEY: process.env.MIRROR_ENVIRONMENT_API_PRIVATE_KEY,
    NEXT_PUBLIC_REMOTE_ENVIRONMENT_NAME: process.env.NEXT_PUBLIC_REMOTE_ENVIRONMENT_NAME,
    NEXT_PUBLIC_MIRROR_ENVIRONMENT_NAME: process.env.NEXT_PUBLIC_MIRROR_ENVIRONMENT_NAME,
    NEXT_PUBLIC_POSTHOG_API_KEY: process.env.NEXT_PUBLIC_POSTHOG_API_KEY,
    FIGMA_CLIENT_ID: process.env.FIGMA_CLIENT_ID,
    FIGMA_CLIENT_SECRET: process.env.FIGMA_CLIENT_SECRET,
    SLACK_WEBHOOK_URL_PORTAL_UPDATES: process.env.SLACK_WEBHOOK_URL_PORTAL_UPDATES,
    TEMPORAL_URL: process.env.TEMPORAL_URL,
    SYNC_PROJECTS_CRON: process.env.SYNC_PROJECTS_CRON,
    DISABLE_APIFY_WORKERS: process.env.DISABLE_APIFY_WORKERS?.trim().toLowerCase() === 'true',
    HEARTBEAT_URL: process.env.HEARTBEAT_URL,
    SCANNER_JOB_HEARTBEAT_CONFIG: process.env.SCANNER_JOB_HEARTBEAT_CONFIG,
    TEMPORAL_API_KEY: process.env.TEMPORAL_API_KEY,
    TEMPORAL_NAMESPACE: process.env.TEMPORAL_NAMESPACE,
    TEMPORAL_WORKER_URL: process.env.TEMPORAL_WORKER_URL,
    WEBHOOK_SECRET_ENCRYPTION_KEY: process.env.WEBHOOK_SECRET_ENCRYPTION_KEY,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_PUBLISHABLE_KEY: process.env.STRIPE_PUBLISHABLE_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    STRIPE_PRICE_ID_TIERED: process.env.STRIPE_PRICE_ID_TIERED,
    PLAID_CLIENT_ID: process.env.PLAID_CLIENT_ID,
    PLAID_SECRET: process.env.PLAID_SECRET,
    PLAID_ENV: process.env.PLAID_ENV,
    COINBASE_CLIENT_ID: process.env.COINBASE_CLIENT_ID,
    COINBASE_CLIENT_SECRET: process.env.COINBASE_CLIENT_SECRET,
    MORALIS_API_KEY: process.env.MORALIS_API_KEY,
    GITBOOK_API_TOKEN: process.env.GITBOOK_API_TOKEN,
    GITBOOK_SPACE_ID: process.env.GITBOOK_SPACE_ID,
    ENABLE_APIFY_RUNNER: process.env.ENABLE_APIFY_RUNNER
      ? process.env.ENABLE_APIFY_RUNNER?.trim().toLowerCase() === 'true'
      : undefined,
    ENABLE_SYNC_PROJECTS: process.env.ENABLE_SYNC_PROJECTS
      ? process.env.ENABLE_SYNC_PROJECTS?.trim().toLowerCase() === 'true'
      : undefined, // set to `undefined` so it can fallback to default
    ENABLE_AGENT_FINETUNING_SCHEDULE: process.env.ENABLE_AGENT_FINETUNING_SCHEDULE
      ? process.env.ENABLE_AGENT_FINETUNING_SCHEDULE?.trim().toLowerCase() === 'true'
      : undefined,
    ENABLE_EMAIL_DIGEST_SCHEDULE: process.env.ENABLE_EMAIL_DIGEST_SCHEDULE
      ? process.env.ENABLE_EMAIL_DIGEST_SCHEDULE?.trim().toLowerCase() === 'true'
      : undefined,
    NEXT_PUBLIC_POSTHOG_API_HOST: process.env.NEXT_PUBLIC_POSTHOG_API_HOST,
    NEXT_PUBLIC_DEV_AUTH_BYPASS:
      process.env.NEXT_PUBLIC_DEV_AUTH_BYPASS?.trim().toLowerCase() === 'true',

    OVERRIDE_AGENT_TENANT_ID: process.env.OVERRIDE_AGENT_TENANT_ID,
    OVERRIDE_AGENT_USER_ID: process.env.OVERRIDE_AGENT_USER_ID,
    OVERRIDE_AGENT_WORKSPACE_ID: process.env.OVERRIDE_AGENT_WORKSPACE_ID,
    DEV_AUTH_BYPASS: process.env.DEV_AUTH_BYPASS?.trim().toLowerCase() === 'true',
    DEV_TENANT_ID: process.env.DEV_TENANT_ID,
    DEV_USER_ID: process.env.DEV_USER_ID,
    DEV_WORKSPACE_ID: process.env.DEV_WORKSPACE_ID,
    AGENT_MODEL_METRICS_API_KEY: process.env.AGENT_MODEL_METRICS_API_KEY,
    MODAL_ORCHESTRATOR_URL: process.env.MODAL_ORCHESTRATOR_URL,
    MASTRA_WORKFLOW_CONCURRENCY: process.env.MASTRA_WORKFLOW_CONCURRENCY
      ? parseInt(process.env.MASTRA_WORKFLOW_CONCURRENCY)
      : undefined,
    DEFAULT_PAGE_SIZE: process.env.DEFAULT_PAGE_SIZE
      ? parseInt(process.env.DEFAULT_PAGE_SIZE)
      : undefined,
    MAX_PAGE_SIZE: process.env.MAX_PAGE_SIZE ? parseInt(process.env.MAX_PAGE_SIZE) : undefined,
    PORT: parseInt(process.env.PORT ?? '3000'),
    MASTRA_PLAYGROUND_URL: process.env.MASTRA_PLAYGROUND_URL,
    PROTEGE_MCP_SERVER_URL: process.env.PROTEGE_MCP_SERVER_URL,
    NEXUS_SERVER_URL: process.env.NEXUS_SERVER_URL,
    ENABLE_ECOMMS_INGEST_SCHEDULE:
      process.env.ENABLE_ECOMMS_INGEST_SCHEDULE?.trim().toLowerCase() === 'true',
    DISABLE_SIMILAR_FEEDBACK: process.env.DISABLE_SIMILAR_FEEDBACK?.trim().toLowerCase() === 'true',
    DISABLE_QDRANT_DOCUMENT_PROCESSING:
      process.env.DISABLE_QDRANT_DOCUMENT_PROCESSING?.trim().toLowerCase() === 'true',
  },

  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
});
